"use client";

import React, { useEffect, useRef, useState, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, useAnimation } from 'framer-motion';

interface Category {
  id: number;
  name: string;
  slug: string;
  image_url?: string | null;
}

interface CategorySliderProps {
  categories: Category[];
  title?: string;
  className?: string;
}

export default function CategorySlider({ 
  categories,
  title = "Shop by Categories",
  className = ""
}: CategorySliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const controls = useAnimation();
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemWidth, setItemWidth] = useState(190); // Slightly bigger images (was 160)
  const [isHovered, setIsHovered] = useState(false);
  
  // Create a duplicate array to ensure smooth infinite scroll
  // Duplicate the list to create a seamless infinite scroll effect
  const displayCategories = [...categories, ...categories, ...categories];
  
  // Function to start or resume the animation
  const startAnimation = useCallback(async () => {
    // Create smooth continuous scroll from starting position
    await controls.start({ x: 0 });
    
    controls.start({
      x: -itemWidth * categories.length, // Move exactly one set of categories
      transition: {
        duration: categories.length * 8, // Much slower animation
        ease: "linear",
        repeat: Infinity,
        repeatType: "loop"
      }
    });
  }, [controls, itemWidth, categories.length]);
  
  // Function to pause the animation
  const pauseAnimation = useCallback(() => {
    controls.stop();
  }, [controls]);
  
  // Handle hover events
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    pauseAnimation();
  }, [pauseAnimation]);
  
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    startAnimation();
  }, [setIsHovered, startAnimation]);
  
  useEffect(() => {
    if (categories.length === 0) return;
    
    // Get container dimensions
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    // Update dimensions initially and on resize
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    // Start the infinite scroll animation (but only if not hovered)
    if (!isHovered) {
      startAnimation();
    }
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
      controls.stop();
    };
  }, [categories.length, controls, itemWidth, isHovered, startAnimation]);
  
  if (categories.length === 0) return null;

  return (
    <div className={`py-10 ${className}`}>
      {title && (
        <h2 className="text-2xl font-bold mb-6 text-center">{title}</h2>
      )}
      
      <div 
        ref={containerRef}
        className="overflow-hidden relative w-full" 
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <motion.div
          className="flex"
          animate={controls}
        >
          {displayCategories.map((category, index) => (
            <div
              key={`${category.id}-${index}`}
              className="flex-shrink-0"
              style={{ width: itemWidth }}
            >
              <Link 
                href={`/category/${category.slug}`}
                className="group flex flex-col items-center mx-2"
              >
                <div className="relative w-[160px] h-[160px] rounded-full overflow-hidden border shadow-sm mb-3 mx-auto">
                  {category.image_url ? (
                    <Image
                      src={category.image_url}
                      alt={category.name}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform"
                      sizes="160px"
                    />
                  ) : (
                    <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                      <span className="font-medium">{category.name}</span>
                    </div>
                  )}
                </div>
                <h3 className="text-center font-medium group-hover:text-primary transition-colors">
                  {category.name}
                </h3>
              </Link>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
